import React, { useCallback, useMemo } from 'react';
import { TypedMemo } from '../../../common/types';
import { SMLUpgradeFeature } from '../../../music24/config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../../music24/hooks/useUpgradePrompt';
import { useOutreachMailboxLimit } from '../../../music24/outreach/hooks/useOutreachMailboxLimit';
import { Panel } from '../../../music24/components/primitives/Panel';
import type { MailboxRow } from '../../../music24/outreach/outreachTypes';
import { MailboxAddRow } from './MailboxAddRow';
import { MailboxEmpty } from './MailboxEmpty';
import { MailboxFacts } from './MailboxFacts';
import { MailboxListHead } from './MailboxListHead';
import { MailboxRights } from './MailboxRights';
import { MailboxRowView } from './MailboxRowView';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Mailboxes card; each part uses its own slice of it */
import styles from './Mailboxes.module.scss';

interface MailboxListProps {
  mailboxes: MailboxRow[];
  isOwner: boolean;
  /** Opens the connect guide (design outreach-v2 o, p). */
  onConnect: () => void;
  /** Opens the connect guide with the address of a connected mailbox ("Change the password"). */
  onChangePassword: (mailbox: MailboxRow) => void;
}

/**
 * The Mailboxes card (design outreach-v2 q1 to q4): the head with the slot pips, one row per
 * mailbox, "Connect another mailbox" while the plan has room (none when the plan is full, q3), the
 * empty state (q4), the two facts and the rights line. A plan with no mailbox opens the plan prompt.
 */
const MailboxListComponent: React.FC<MailboxListProps> = ({ mailboxes, isOwner, onConnect, onChangePassword }) => {
  const limit = useOutreachMailboxLimit();
  const openUpgradePrompt = useUpgradePrompt();
  const room = Math.max(0, limit.own - mailboxes.length);
  const connect = useCallback(() => {
    if (room === 0) {
      openUpgradePrompt(SMLUpgradeFeature.outreachMailboxes);
      return;
    }
    onConnect();
  }, [onConnect, openUpgradePrompt, room]);
  const rows = useMemo(
    () =>
      mailboxes.map((mailbox) => (
        <MailboxRowView
          key={mailbox.mailboxUuid}
          mailbox={mailbox}
          isOwner={isOwner}
          onChangePassword={onChangePassword}
        />
      )),
    [isOwner, mailboxes, onChangePassword]
  );
  return (
    <Panel as="section" className={styles.card} data-testid="mailboxes-card">
      <MailboxListHead used={mailboxes.length} limit={limit.own} />
      {mailboxes.length === 0 ? <MailboxEmpty onConnect={connect} /> : rows}
      {mailboxes.length > 0 && room > 0 && <MailboxAddRow room={room} onConnect={connect} />}
      <MailboxFacts />
      <MailboxRights isOwner={isOwner} />
    </Panel>
  );
};

export const MailboxList = TypedMemo(MailboxListComponent);
